import { doc, onSnapshot } from 'firebase/firestore'
import {
  bridgeCoverageProjectionSchema,
  type BridgeCoverageProjection,
  type ConsultationId,
} from '@gh/common/models'
import { firestore } from '@/firebase'
import {
  BridgeCoverageFailureError,
  BridgeCoverageWaitTimeoutError,
} from '@/bridge/bridgeCoverageErrors'
import {
  bridgeCoverageFailureMessage,
  isTerminalBridgeServiceEligibilityStatus,
} from '@/bridge/bridgeServiceEligibilityTerminal'
import createLogger from '@/utils/logger'

const logger = createLogger('bridge/bridgeCoverageProjectionWait')

const DEFAULT_BRIDGE_COVERAGE_TIMEOUT_MS = 45_000

export const BRIDGE_COVERAGE_WAIT_TIMEOUT_MESSAGE =
  'Coverage verification is taking longer than expected. Please try again in a moment.'

type CoverageWaitDebugState = {
  status?: string
  inSync?: boolean
  assignedProviderEligible?: boolean
  pollOutcome?: string
  reservedSlotKey?: string
  seSyncId?: string
  patientResponsibility?: number
  updatedAt?: string
}

function coverageWaitDebug(message: string, data?: Record<string, unknown>) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[bridgeCoverageWait]', message, data ?? '')
  }
  logger.info(message, data)
}

function toIsoDebugTimestamp(value: unknown): string {
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (
    value != null &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  ) {
    try {
      return (value as { toDate: () => Date }).toDate().toISOString()
    } catch {
      // fall through
    }
  }
  return String(value)
}

function projectionDebugState(
  projection: BridgeCoverageProjection | null,
): CoverageWaitDebugState | undefined {
  if (!projection) return undefined
  return {
    status: projection.status,
    inSync: projection.inSync,
    assignedProviderEligible: projection.assignedProviderEligible,
    pollOutcome: projection.pollOutcome,
    reservedSlotKey: projection.reservedSlotKey,
    seSyncId: projection.seSyncId,
    patientResponsibility: projection.patientResponsibility,
    updatedAt: projection.updatedAt != null ? toIsoDebugTimestamp(projection.updatedAt) : undefined,
  }
}

function describeCoverageWaitBlocker(args: {
  projection: BridgeCoverageProjection
  expectedReservedSlotKey?: string
}): string {
  const { projection, expectedReservedSlotKey } = args
  if (expectedReservedSlotKey && projection.reservedSlotKey !== expectedReservedSlotKey) {
    return `reservedSlotKey mismatch (expected=${expectedReservedSlotKey}, got=${projection.reservedSlotKey ?? 'undefined'})`
  }
  if (
    projection.status &&
    isTerminalBridgeServiceEligibilityStatus(projection.status) &&
    projection.status !== 'ELIGIBLE'
  ) {
    return `terminal status ${projection.status}`
  }
  if (
    projection.pollOutcome === 'timeout' &&
    !(projection.inSync === true && projection.status === 'ELIGIBLE')
  ) {
    return `pollOutcome timeout (status=${projection.status ?? 'undefined'}, inSync=${String(projection.inSync)})`
  }
  if (projection.inSync !== true) {
    return `waiting for inSync (status=${projection.status ?? 'undefined'})`
  }
  if (projection.status === 'ELIGIBLE' && projection.assignedProviderEligible === false) {
    return 'assigned provider not eligible for coverage'
  }
  if (projection.status !== 'ELIGIBLE') {
    return `waiting for ELIGIBLE (status=${projection.status ?? 'undefined'})`
  }
  if (projection.patientResponsibility == null) {
    return 'waiting for patientResponsibility on ELIGIBLE'
  }
  return 'ready'
}

/**
 * Wait until `consultations/{consultationId}/projections/bridgeCoverage` is
 * inSync, ELIGIBLE, and has `patientResponsibility` for the reserved slot —
 * server begin_payment stamps that amount; ready without it would fail confirm.
 */
export async function waitForBridgeCoverageReady(args: {
  consultationId: ConsultationId
  reservedSlotKey?: string
  timeoutMs?: number
}): Promise<BridgeCoverageProjection> {
  const timeoutMs = args.timeoutMs ?? DEFAULT_BRIDGE_COVERAGE_TIMEOUT_MS
  const ref = doc(firestore, 'consultations', args.consultationId, 'projections', 'bridgeCoverage')
  const startedAt = Date.now()

  coverageWaitDebug('wait started', {
    consultationId: args.consultationId,
    reservedSlotKey: args.reservedSlotKey,
    timeoutMs,
  })

  return new Promise((resolve, reject) => {
    let settled = false
    let snapshotCount = 0
    let missingDocLogged = false
    let lastSeen: CoverageWaitDebugState | undefined
    let lastBlocker: string | undefined

    const logTimeout = (reason: 'client_timeout' | 'poll_outcome_timeout') => {
      const elapsedMs = Date.now() - startedAt
      const payload = {
        reason,
        consultationId: args.consultationId,
        reservedSlotKey: args.reservedSlotKey,
        timeoutMs,
        elapsedMs,
        snapshotCount,
        lastSeen,
        lastBlocker,
      }
      coverageWaitDebug('wait timed out', payload)
      logger.error('Bridge coverage projection wait timed out', payload)
    }

    const listeners = {
      unsubscribe: undefined as (() => void) | undefined,
    }

    const unsubscribe = () => {
      if (listeners.unsubscribe) {
        listeners.unsubscribe()
      } else {
        Promise.resolve().then(() => listeners.unsubscribe?.())
      }
    }

    const timeout = setTimeout(() => {
      if (settled) return
      settled = true
      unsubscribe()
      logTimeout('client_timeout')
      reject(new BridgeCoverageWaitTimeoutError(BRIDGE_COVERAGE_WAIT_TIMEOUT_MESSAGE))
    }, timeoutMs)

    listeners.unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (settled) return
        snapshotCount += 1

        if (!snapshot.exists()) {
          if (!missingDocLogged) {
            missingDocLogged = true
            coverageWaitDebug('projection doc missing (waiting for first write)', {
              consultationId: args.consultationId,
              snapshotCount,
            })
          }
          return
        }

        try {
          const projection = bridgeCoverageProjectionSchema.parse({
            id: snapshot.id,
            ...snapshot.data(),
          })
          const debugState = projectionDebugState(projection)
          const blocker = describeCoverageWaitBlocker({
            projection,
            expectedReservedSlotKey: args.reservedSlotKey,
          })

          if (blocker !== lastBlocker || snapshotCount <= 3) {
            lastBlocker = blocker
            coverageWaitDebug('projection snapshot', {
              consultationId: args.consultationId,
              snapshotCount,
              elapsedMs: Date.now() - startedAt,
              blocker,
              ...debugState,
            })
          }
          lastSeen = debugState

          if (args.reservedSlotKey && projection.reservedSlotKey !== args.reservedSlotKey) {
            return
          }

          if (
            projection.status &&
            isTerminalBridgeServiceEligibilityStatus(projection.status) &&
            projection.status !== 'ELIGIBLE'
          ) {
            settled = true
            clearTimeout(timeout)
            unsubscribe()
            coverageWaitDebug('wait rejected (terminal coverage status)', {
              consultationId: args.consultationId,
              status: projection.status,
              elapsedMs: Date.now() - startedAt,
              snapshotCount,
            })
            reject(
              new BridgeCoverageFailureError(
                bridgeCoverageFailureMessage(projection.status),
                projection.status,
              ),
            )
            return
          }

          if (
            projection.pollOutcome === 'timeout' &&
            !(projection.inSync === true && projection.status === 'ELIGIBLE')
          ) {
            settled = true
            clearTimeout(timeout)
            unsubscribe()
            logTimeout('poll_outcome_timeout')
            reject(new BridgeCoverageWaitTimeoutError(BRIDGE_COVERAGE_WAIT_TIMEOUT_MESSAGE))
            return
          }

          if (
            projection.inSync === true &&
            projection.status === 'ELIGIBLE' &&
            projection.assignedProviderEligible === false
          ) {
            settled = true
            clearTimeout(timeout)
            unsubscribe()
            coverageWaitDebug('wait rejected (assigned provider ineligible)', {
              consultationId: args.consultationId,
              elapsedMs: Date.now() - startedAt,
              snapshotCount,
            })
            reject(
              new BridgeCoverageFailureError(
                bridgeCoverageFailureMessage('INELIGIBLE'),
                'INELIGIBLE',
              ),
            )
            return
          }

          if (
            projection.inSync === true &&
            projection.status === 'ELIGIBLE' &&
            projection.patientResponsibility != null &&
            projection.assignedProviderEligible === true
          ) {
            settled = true
            clearTimeout(timeout)
            unsubscribe()
            coverageWaitDebug('wait resolved ELIGIBLE', {
              consultationId: args.consultationId,
              elapsedMs: Date.now() - startedAt,
              snapshotCount,
              patientResponsibility: projection.patientResponsibility,
            })
            resolve(projection)
          }
        } catch (error) {
          settled = true
          clearTimeout(timeout)
          unsubscribe()
          logger.error('Bridge coverage projection parse error', {
            consultationId: args.consultationId,
            snapshotCount,
            elapsedMs: Date.now() - startedAt,
            error,
          })
          reject(error instanceof Error ? error : new Error(String(error)))
        }
      },
      (error) => {
        if (settled) return
        settled = true
        clearTimeout(timeout)
        unsubscribe()
        logger.error('Bridge coverage projection snapshot error', {
          consultationId: args.consultationId,
          snapshotCount,
          elapsedMs: Date.now() - startedAt,
          error,
        })
        reject(error)
      },
    )
  })
}
